import { apiClient, ApiResponse } from './api';
import { useAuthStore } from '@/store/auth.store';
import { useProfileStore } from '@/store/profile.store';
import { localVacancyService } from './local/vacancy.local';

export type VacancySource = 'linkedin' | 'hh' | 'indeed' | 'ats' | 'other';

export interface Vacancy {
  id: string;
  userId: string;
  title: string;
  company: string;
  location?: string;
  salary?: string;
  employmentType?: string;
  experienceLevel?: string;
  description: string;
  requirements?: string;
  responsibilities?: string;
  benefits?: string;
  skills: string[];
  url?: string;
  source: VacancySource;
  matchScore?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface VacancyListFilters {
  source?: VacancySource;
  minMatchScore?: number;
  maxMatchScore?: number;
  company?: string;
  search?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'createdAt' | 'matchScore' | 'title' | 'company';
  sortOrder?: 'asc' | 'desc';
}

export interface VacancyStats {
  total: number;
  bySource: Record<VacancySource, number>;
  averageMatchScore: number;
  highMatchCount: number;
}

export interface ParseVacancyRequest {
  text: string;
  url?: string;
  source?: VacancySource;
  saveToLibrary?: boolean;
}

export interface VacancyListResponse {
  vacancies: Vacancy[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface ParsedVacancy {
  title: string;
  company: string;
  location?: string;
  salary?: string;
  employmentType?: string;
  experienceLevel?: string;
  description: string;
  /** Nested requirements returned by AI parsing. skills.required and skills.preferred are arrays. */
  requirements?: {
    skills?: {
      required?: string[];
      preferred?: string[];
    };
    experience?: {
      years?: number;
      type?: string;
      requiredPositions?: string[];
    };
  };
  responsibilities?: string;
  benefits?: string;
  /** @deprecated Use requirements.skills.required/preferred instead */
  skills?: string[];
}

export interface MatchDetails {
  vacancyId: string;
  profileId: string;
  matchScore: number;  // 0-100
  breakdown: {
    skills: {
      score: number;
      matched: string[];
      missing: string[];
      percentage: number;
    };
    experience: {
      score: number;
      matched: string[];
      missing: string[];
      years: number;
    };
    education: {
      score: number;
      matched: boolean;
      details: string;
    };
  };
  strengths: string[];
  weaknesses: string[];
  recommendations: Array<{
    type: 'add' | 'emphasize' | 'reword';
    section: string;
    suggestion: string;
    reason: string;
  }>;
}

export const vacancyService = {
  /**
   * Get user's vacancies with optional filters
   */
  async listVacancies(filters?: VacancyListFilters): Promise<{
    vacancies: Vacancy[];
    pagination: { total: number; limit: number; offset: number };
  }> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      return localVacancyService.list();
    }

    const params = new URLSearchParams();
    if (filters?.source) params.append('source', filters.source);
    if (filters?.minMatchScore) params.append('minMatchScore', String(filters.minMatchScore));
    if (filters?.maxMatchScore) params.append('maxMatchScore', String(filters.maxMatchScore));
    if (filters?.company) params.append('company', filters.company);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<
      ApiResponse<{
        vacancies: Vacancy[];
        pagination: { total: number; limit: number; offset: number };
      }>
    >(`/api/v1/vacancy${query}`);
    return response.data.data;
  },

  /**
   * Get vacancy statistics
   */
  async getStats(): Promise<VacancyStats> {
    const response = await apiClient.get<ApiResponse<VacancyStats>>('/api/v1/vacancy/stats');
    return response.data.data;
  },

  /**
   * Get recent vacancies
   */
  async getRecent(limit = 10): Promise<Vacancy[]> {
    const response = await apiClient.get<ApiResponse<{ vacancies: Vacancy[] }>>(
      `/api/v1/vacancy/recent?limit=${limit}`
    );
    return response.data.data.vacancies;
  },

  /**
   * Get vacancies with highest match scores
   */
  async getBestMatches(minScore = 70, limit = 10): Promise<Vacancy[]> {
    const response = await apiClient.get<ApiResponse<{ vacancies: Vacancy[] }>>(
      `/api/v1/vacancy/best-matches?minScore=${minScore}&limit=${limit}`
    );
    return response.data.data.vacancies;
  },

  /**
   * Parse vacancy description using AI
   */
  async parseVacancy(
    request: ParseVacancyRequest
  ): Promise<{ parsed: ParsedVacancy; vacancy?: Vacancy; saved: boolean }> {
    // Backend expects 'description' and 'save' fields; ParseVacancyRequest uses 'text' and 'saveToLibrary'
    const payload = {
      description: request.text,
      url: request.url,
      source: request.source,
      save: request.saveToLibrary ?? false,
    };
    const response = await apiClient.post<
      ApiResponse<{ parsed: ParsedVacancy; vacancy?: Vacancy; saved: boolean }>
    >('/api/v1/vacancy/parse', payload);
    return response.data.data;
  },

  /**
   * Get vacancy by ID
   */
  async getVacancy(id: string): Promise<Vacancy> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      const list = await localVacancyService.list();
      const found = list.vacancies.find((v) => v.id === id);
      if (!found) throw new Error('Vacancy not found in local mode');
      return found;
    }

    const response = await apiClient.get<ApiResponse<Vacancy>>(`/api/v1/vacancy/${id}`);
    return response.data.data;
  },

  /**
   * Create new vacancy
   */
  async createVacancy(data: Partial<Vacancy>): Promise<Vacancy> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      const vacancy: Vacancy = {
        id: `local-${Date.now()}`,
        userId: 'local-user',
        title: data.title || 'Untitled vacancy',
        company: data.company || 'Unknown',
        description: data.description || '',
        skills: data.skills || [],
        source: data.source || 'other',
        location: data.location,
        salary: data.salary,
        employmentType: data.employmentType,
        experienceLevel: data.experienceLevel,
        requirements: data.requirements,
        responsibilities: data.responsibilities,
        benefits: data.benefits,
        url: data.url,
        matchScore: data.matchScore,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      await localVacancyService.add(vacancy);
      return vacancy;
    }

    const response = await apiClient.post<ApiResponse<Vacancy>>('/api/v1/vacancy', data);
    return response.data.data;
  },

  /**
   * Update vacancy
   */
  async updateVacancy(id: string, data: Partial<Vacancy>): Promise<Vacancy> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      throw new Error('Vacancy update is available only in cloud mode');
    }
    const response = await apiClient.put<ApiResponse<Vacancy>>(`/api/v1/vacancy/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete vacancy
   */
  async deleteVacancy(id: string): Promise<void> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      await localVacancyService.remove(id);
      return;
    }
    await apiClient.delete(`/api/v1/vacancy/${id}`);
  },

  /**
   * Match vacancy with user's profile
   * ALWAYS calls server for AI matching, sending profileData if not authenticated
   */
  async matchVacancy(id: string, options?: { profileId?: string }): Promise<MatchDetails> {
    const { isAuthenticated } = useAuthStore.getState();

    // Build request body
    const requestBody: { profileId?: string; profileData?: any } = { ...options };

    // If not authenticated, include profileData from localStorage
    if (!isAuthenticated) {
      const { profile } = useProfileStore.getState();
      if (!profile) {
        throw new Error('Profile not found. Please create a profile first.');
      }
      requestBody.profileData = profile;
    }

    const response = await apiClient.post<ApiResponse<MatchDetails>>(
      `/api/v1/vacancy/${id}/match`,
      requestBody
    );
    return response.data.data;
  },

  /**
   * Re-parse existing vacancy
   */
  async reparseVacancy(id: string): Promise<Vacancy> {
    const response = await apiClient.post<ApiResponse<Vacancy>>(`/api/v1/vacancy/${id}/reparse`);
    return response.data.data;
  },
};
